import { useGetTaskOptionsQuery, useRunTaskMutation } from '@/services/adminTasks';
import {
  Box,
  Button,
  FormikCheckboxInput,
  FormikSelectInput,
  FormikSelectInputNative,
  Icon,
  TextLink,
  toast,
} from '@palmetto/palmetto-components';
import { Field, Form, Formik } from 'formik';
import { useState } from 'react';

import { AdminTask, ProgramType, ProgramTypeLabels, VendorEquipmentTypes } from 'types';

interface ProgramTypeOption {
  value: ProgramType;
  label: string;
}

interface AvlImportFormValues {
  sheetId: string;
  programTypes: ProgramTypeOption[];
  inverter: boolean;
  panel: boolean;
  storage: boolean;
  mounting: boolean;
  commit: boolean;
}

export const AvlImport = () => {
  const { data: spreadsheetConfig, isLoading } = useGetTaskOptionsQuery({ name: AdminTask.avlImport });
  const [runTask] = useRunTaskMutation();
  const [results, setResults] = useState<any>(null);

  const handleSubmit = async (values: AvlImportFormValues) => {
    const toastId = toast.loading('Importing AVL');
    try {
      const hardware: VendorEquipmentTypes[] = [];
      if (values.inverter) hardware.push(VendorEquipmentTypes.inverter);
      if (values.panel) hardware.push(VendorEquipmentTypes.panel);
      if (values.storage) hardware.push(VendorEquipmentTypes.storage);
      if (values.mounting) hardware.push(VendorEquipmentTypes.mounting);

      const result = await runTask({
        task: 'avlImport',
        spreadsheetId: values.sheetId,
        programTypes: values.programTypes.map((pt) => pt.value),
        hardware,
        commit: values.commit,
      }).unwrap();
      setResults(result);
      toast.success('Imported AVL data successfully');
    } catch (e: any) {
      console.error(e);
      toast.error(e?.data?.message || 'Error importing AVL data');
    } finally {
      toast.dismiss(toastId);
    }
  };

  if (isLoading || !spreadsheetConfig) {
    return <div>Loading...</div>;
  }

  const programTypeOptions = Object.values([ProgramType.solar, ProgramType.newHomes, ProgramType.newCommunity]).map(
    (type) => ({
      value: type,
      label: ProgramTypeLabels[type],
    }),
  );

  const spreadsheetOptions = spreadsheetConfig.spreadsheets?.map((option: any) => ({
    value: option.spreadsheetId,
    label: `${option.name} (${option.spreadsheetId})`,
  }));

  const onlyOneSpreadsheet = spreadsheetOptions.length === 1;

  const initialValues: AvlImportFormValues = {
    sheetId: spreadsheetConfig.spreadsheets[0]?.spreadsheetId,
    programTypes: programTypeOptions,
    inverter: false,
    panel: false,
    storage: false,
    mounting: false,
    commit: false,
  };

  return (
    <Box padding="xl">
      <Formik initialValues={initialValues} enableReinitialize onSubmit={handleSubmit}>
        {({ isSubmitting, values }) => (
          <Form>
            <Box gap="md">
              <Field
                id="sheetId"
                name="sheetId"
                label="Source Google Sheet"
                helpText={
                  <TextLink
                    href={`https://docs.google.com/spreadsheets/d/${values.sheetId}/edit`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <span>Open Spreadsheet</span>
                    <Icon name="launch-app" size="xs" />
                  </TextLink>
                }
                component={FormikSelectInputNative}
                options={spreadsheetOptions}
                isDisabled={isSubmitting || onlyOneSpreadsheet}
              />

              <Field
                id="programTypes"
                name="programTypes"
                label="Program Types"
                component={FormikSelectInput}
                options={programTypeOptions}
                isMulti
                isDisabled={isSubmitting}
              />
              <Box as="fieldset" direction="row" justifyContent="space-around">
                <Box as="legend" fontWeight="medium" fontSize="md">
                  Hardware Types
                </Box>
                <Field
                  label="Inverter"
                  name="inverter"
                  id="inverter"
                  component={FormikCheckboxInput}
                  isChecked={values.inverter}
                />
                <Field
                  label="Panel"
                  name="panel"
                  id="panel"
                  component={FormikCheckboxInput}
                  isChecked={values.panel}
                />
                <Field
                  label="Storage"
                  name="storage"
                  id="storage"
                  component={FormikCheckboxInput}
                  isChecked={values.storage}
                />
                <Field
                  label="Mounting"
                  name="mounting"
                  id="mounting"
                  component={FormikCheckboxInput}
                  isChecked={values.mounting}
                />
              </Box>
              <Box direction="row" alignItems="flex-end" gap="md">
                <Field
                  label="Commit changes (otherwise will run in dry-run mode)"
                  name="commit"
                  id="commit"
                  component={FormikCheckboxInput}
                  isChecked={values.commit}
                />
                <Button
                  size="md"
                  variant="primary"
                  type="submit"
                  isLoading={isSubmitting}
                  disabled={
                    (!values.inverter && !values.panel && !values.storage && !values.mounting) ||
                    values.programTypes.length === 0 ||
                    isSubmitting
                  }
                >
                  Import AVL
                </Button>
              </Box>
              {results && (
                <Box overflow="auto" height="600px">
                  <pre>{JSON.stringify(results, null, 2)}</pre>
                </Box>
              )}
            </Box>
          </Form>
        )}
      </Formik>
    </Box>
  );
};
